import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { YdsCardComponent, YdsSpinnerComponent, YdsTableComponent, type YdsTableColumn } from '../yds-angular';
import { DashboardService } from './dashboard.service';
import { DashboardInfo, Institution, TenantInfo } from '../model/dashboard.model';
import { formatDisplayDate } from '../shared/general-ledger-yds.utils';

interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

@Component({
  selector: 'app-home',
  imports: [YdsCardComponent, YdsSpinnerComponent, YdsTableComponent],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home {
  private dashboardService = inject(DashboardService);

  readonly institutionColumns: YdsTableColumn[] = [
    { key: 'name', header: 'Naam', sortable: true },
    { key: 'code', header: 'Code', sortable: true },
  ];

  readonly importRunColumns: YdsTableColumn[] = [
    { key: 'institution', header: 'Instelling', sortable: true },
    { key: 'yearPeriod', header: 'Jaar/Periode', sortable: true },
    { key: 'runNumber', header: 'Verwerkingsnummer', sortable: true },
  ];

  readonly planningRecordColumns: YdsTableColumn[] = [
    { key: 'institution', header: 'Instelling', sortable: true },
    { key: 'forReporting', header: 'Rapportage', sortable: true },
    { key: 'salarisPeriode', header: 'Salaris periode', sortable: true },
    { key: 'salarisrun', header: 'Salarisrun', sortable: true },
    { key: 'status', header: 'Status', sortable: true },
    { key: 'datumTijd', header: 'Datum+tijd', sortable: true },
  ];

  readonly isLoading = signal(true);
  private readonly dashboard = signal<DashboardInfo>({ tenants: [], latestImportRuns: [], latestPlanningRecords: [] });
  private readonly tenantInfo = signal<TenantInfo[]>([]);

  /** Maps a tenant code to its onboarding status. */
  private readonly onboardingStatusByCode = computed(() => {
    const map = new Map<number, string>();
    for (const info of this.tenantInfo()) {
      map.set(info.code, info.onboardingStatus);
    }
    return map;
  });

  /** Sort state per payroll client id for the institution tables. */
  private readonly institutionSort = signal<Record<string, SortState>>({});
  private readonly importRunSort = signal<SortState | null>(null);
  private readonly planningRecordSort = signal<SortState | null>(null);

  readonly tenants = computed(() => this.dashboard().tenants);
  readonly latestImportRuns = computed(() => this.dashboard().latestImportRuns);
  readonly latestPlanningRecords = computed(() => this.dashboard().latestPlanningRecords.slice(0, 10));

  readonly importRunRows = computed<Record<string, unknown>[]>(() => {
    const rows = this.latestImportRuns().map((run) => ({
      institution: run.institutionName,
      yearPeriod: `${run.year}/${this.pad2(run.period)}`,
      runNumber: `${run.payrollRunNumber}/${run.payrollSequence}`,
    }));
    return this.sortRows(rows, this.importRunSort());
  });

  readonly planningRecordRows = computed<Record<string, unknown>[]>(() => {
    const rows = this.latestPlanningRecords().map((record) => ({
      institution: record.institutionName,
      forReporting: record.forReporting ? 'Ja' : 'Nee',
      salarisPeriode: `${record.payrollYear}/${this.pad2(record.payrollPeriod)}`,
      salarisrun: `${record.payrollRunNumber}/${record.payrollSequence}`,
      status: record.processingStatus,
      datumTijd: this.formatDateTime(record.changeDateTime),
    }));
    return this.sortRows(rows, this.planningRecordSort());
  });

  constructor() {
    this.dashboardService
      .getDashboardInfo()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (info) => {
          this.dashboard.set(info);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      });

    this.dashboardService
      .getTenantInfo()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (info) => this.tenantInfo.set(info),
      });
  }

  onboardingStatus(tenantCode: number): string {
    return this.onboardingStatusByCode().get(tenantCode) ?? '';
  }

  institutionRows(payrollClientId: string, institutions: Institution[]): Record<string, unknown>[] {
    const rows = institutions.map((institution) => ({
      name: institution.institutionName,
      code: institution.institutionCode,
    }));
    return this.sortRows(rows, this.institutionSort()[payrollClientId] ?? null);
  }

  onInstitutionSort(payrollClientId: string, sort: SortState): void {
    this.institutionSort.update((state) => ({ ...state, [payrollClientId]: sort }));
  }

  onImportRunSort(sort: SortState): void {
    this.importRunSort.set(sort);
  }

  onPlanningRecordSort(sort: SortState): void {
    this.planningRecordSort.set(sort);
  }

  /** Scrolls to an in-page section instead of letting the anchor navigate/reload. */
  scrollToSection(id: string, event: Event): void {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private sortRows(
    rows: Record<string, unknown>[],
    sort: SortState | null,
  ): Record<string, unknown>[] {
    if (!sort) {
      return rows;
    }
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = a[sort.key];
      const right = b[sort.key];
      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * factor;
      }
      return String(left).localeCompare(String(right), 'nl', { numeric: true }) * factor;
    });
  }

  /** Pads a period to two digits with a leading zero. */
  pad2(period: number): string {
    return period.toString().padStart(2, '0');
  }

  private formatDateTime(value: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return `${formatDisplayDate(date, 'nl-NL')} ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  }
}
